// frontend/src/pages/Rosters/RosterViewPage.js
// View a roster's current members, allow add/remove scholars, save, and delete the roster.
// Filters & search match StudentSelectPage behavior via studentsApi.
// This version avoids 405/429s by calling only supported endpoints on your backend.

import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import { studentsApi } from "../../api/studentsApi";
import { rostersApi } from "../../api/rostersApi";

// --- Same helpers as StudentSelectPage ---
const GRADE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1));

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

// Grade normalizer (kept in sync with StudentSelectPage)
function normalizeGrade(input) {
  if (input === null || input === undefined) return null;
  const raw = String(input).trim();
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    if (n < 0 || n > 12) return null;
    return n;
  }
  const digits = raw.replace(/[^0-9]/g, "");
  if (!digits) return null;
  const n = Number(digits);
  if (Number.isNaN(n)) return null;
  if (n < 0 || n > 12) return null;
  return n;
}

// ---- Moved OUTSIDE component to satisfy react-hooks/exhaustive-deps ----
function normalizeFromRosterStudent(s) {
  if (!s) return null;
  const sid = s.id ?? s.student_id ?? s.uuid ?? s.pk ?? s.scholar_id;
  const joined = [s.first_name, s.last_name].filter(Boolean).join(" ").trim();
  const primary = s.full_name !== undefined && s.full_name !== null ? s.full_name : s.name;
  const fallbackName = joined ? joined : sid != null ? `Student ${sid}` : "Student";
  const name = primary !== undefined && primary !== null ? primary : fallbackName;
  const house = (s.house && s.house.name) || s.house_name || s.house || "";
  const gradeVal = s.grade ?? s.grade_level ?? "";
  return { id: sid, name, house, grade: gradeVal };
}

function extractMembersFromRosterData(r) {
  if (!r) return [];
  const arr =
    r.students ||
    r.members ||
    r.roster_students ||
    r.student_list ||
    [];
  if (Array.isArray(arr) && arr.length > 0) {
    if (typeof arr[0] === "object") {
      return arr.map(normalizeFromRosterStudent).filter(Boolean);
    }
    return arr.map((sid) => ({ id: sid, name: `Student ${sid}` }));
  }
  const ids = r.student_ids || [];
  return ids.map((sid) => ({ id: sid, name: `Student ${sid}` }));
}

export default function RosterViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [roster, setRoster] = useState(null);

  // Members of this roster (id -> minimal student obj { id, name, house?, grade? })
  const [memberMap, setMemberMap] = useState(new Map());
  const [initialMemberIds, setInitialMemberIds] = useState(new Set()); // for diffing adds/removals

  // House & grade dropdowns (match StudentSelectPage)
  const [houses, setHouses] = useState([]); // [{id,name}]
  const [allGrades, setAllGrades] = useState(GRADE_OPTIONS);

  // Filters + search (match StudentSelectPage)
  const [search, setSearch] = useState("");
  const [houseId, setHouseId] = useState("");
  const [grade, setGrade] = useState("");

  // Pagination for the "Add Scholars" results
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const debouncedSearch = useDebounced(search, 300);

  // Query identical to StudentSelectPage (but used for the Add Scholars results)
  const query = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (debouncedSearch) p.q = debouncedSearch;
    if (houseId) p.house_id = houseId; // numeric id
    if (grade) {
      const ng = normalizeGrade(grade);
      if (ng !== null) p.grade = ng;
      else p.grade = grade;
    }
    return p;
  }, [debouncedSearch, houseId, grade, page, pageSize]);

  // ---- Initial load: roster + members ----
  useEffect(() => {
    let mounted = true;
    (async () => {
      setBusy(true);
      setError("");
      setNotice("");
      try {
        // ✅ ONLY use /api/groups/:id/ (DRF router). Avoid /api/rosters/:id to prevent 405s.
        const res = await api.get(`/api/groups/${id}/`);
        if (!(res && res.status >= 200 && res.status < 300 && res.data)) {
          throw new Error("Roster not found");
        }
        if (!mounted) return;

        const data = res.data;
        setRoster(data);

        const members = extractMembersFromRosterData(data);
        const map = new Map();
        const startIds = new Set();
        members.forEach((m) => {
          if (m && m.id != null) {
            map.set(String(m.id), m);
            startIds.add(String(m.id));
          }
        });
        setMemberMap(map);
        setInitialMemberIds(startIds);
      } catch (e) {
        if (mounted) setError((e && e.message) || "Unable to load roster.");
      } finally {
        if (mounted) setBusy(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  const memberList = useMemo(() => Array.from(memberMap.values()), [memberMap]);

  // ---- Load Houses like StudentSelectPage ----
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const h = await api.get("/api/houses/summary", { auth: false });
        const results = Array.isArray(h && h.data && h.data.results)
          ? h.data.results
          : [];
        if (alive) {
          setHouses(results.map((r) => ({ id: String(r.id), name: r.name })));
        }
      } catch {
        // All Houses still works
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // ---- Search for students to add (matches StudentSelectPage query rules) ----
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);

  async function loadSearch() {
    // If no filters and no search, clear results
    const hasFilters = Boolean(houseId || grade);
    if (!debouncedSearch && !hasFilters) {
      setResults([]);
      setTotalCount(0);
      return;
    }

    setSearching(true);
    setError("");
    try {
      const res = await studentsApi.list(query);
      if (!res.ok) {
        setResults([]);
        setTotalCount(0);
        return;
      }
      const list = res.students || []; // already normalized: {id, name, house, grade}

      // Remove already-added members
      const filtered = list.filter((s) => !memberMap.has(String(s.id)));
      setResults(filtered);
      setTotalCount(typeof res.count === "number" ? res.count : filtered.length);

      // Accumulate grades into dropdown (same behavior as StudentSelectPage)
      if (Array.isArray(list) && list.length > 0) {
        setAllGrades((prev) => {
          const set = new Set(prev);
          list.forEach((r) => {
            const raw = String(r.grade ?? "").trim();
            const ng = normalizeGrade(raw);
            if (ng !== null) set.add(String(ng));
          });
          const arr = Array.from(set);
          arr.sort((a, b) => Number(a) - Number(b));
          return arr;
        });
      }
    } catch {
      setResults([]);
      setTotalCount(0);
    } finally {
      setSearching(false);
    }
  }

  // Reset page when filters/search change (to avoid empty pages)
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, houseId, grade]);

  // Trigger search when query/filters/paging change or members change
  useEffect(() => {
    loadSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(query), memberMap]);

  // ---- Member actions ----
  const onRemove = (sid) => {
    setMemberMap((prev) => {
      const m = new Map(prev);
      m.delete(String(sid));
      return m;
    });
  };

  const onAdd = (student) => {
    if (!student || student.id == null) return;
    setMemberMap((prev) => {
      const m = new Map(prev);
      m.set(String(student.id), {
        id: student.id,
        name: student.name,
        house: student.house,
        grade: student.grade,
      });
      return m;
    });
    setSearch("");
  };

  // ---- Save (use only supported endpoints; avoid retries that cause 405/429)
  async function saveMembership() {
    setSaving(true);
    setError("");
    setNotice("");

    const desiredIds = memberList
      .map((s) => Number(s.id))
      .filter((n) => !Number.isNaN(n));
    const desiredSet = new Set(desiredIds.map(String));
    const initialSet = initialMemberIds;

    // compute diffs (for the add-only fallback)
    const toAdd = desiredIds.filter((idNum) => !initialSet.has(String(idNum)));
    const toRemove = Array.from(initialSet).filter(
      (sid) => !desiredSet.has(String(sid))
    );

    // 1) Primary: PATCH /api/groups/:id/ with { student_ids: [...] }  (replaces full membership)
    try {
      const res = await api.patch(`/api/groups/${id}/`, {
        student_ids: desiredIds,
      });
      if (res && res.status >= 200 && res.status < 300) {
        setInitialMemberIds(new Set(desiredIds.map(String))); // sync baseline
        setNotice("Roster saved.");
        setSaving(false);
        return true;
      }
    } catch (e) {
      // If explicitly not allowed, we’ll attempt the add-only fallback once
      if (e && e.status !== 404 && e.status !== 405) {
        setError("Could not save roster membership via /api/groups/:id/.");
        setSaving(false);
        return false;
      }
    }

    // 2) Fallback: POST /api/rosters/:id/students/ with only the added IDs (server adds only)
    if (toAdd.length > 0) {
      try {
        const res = await api.post(`/api/rosters/${id}/students/`, {
          student_ids: toAdd,
        });
        if (res && res.status >= 200 && res.status < 300) {
          const newBase = new Set([
            ...Array.from(initialSet),
            ...toAdd.map(String),
          ]);
          setInitialMemberIds(newBase);
          setNotice(
            toRemove.length > 0
              ? "Added new scholars. (Removals couldn’t be applied on this server.)"
              : "Added new scholars."
          );
          setSaving(false);
          return true;
        }
      } catch {
        // fall through to error below
      }
    }

    // If we get here, nothing worked
    setError(
      "Could not save roster membership. This server expects updates via PATCH /api/groups/:id/ with { student_ids: [...] }. I also tried the add-only fallback at /api/rosters/:id/students/."
    );
    setSaving(false);
    return false;
  }

  // ---- Delete (supported at /api/rosters/:id and /api/groups/:id; try both safely)
  async function onDelete() {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this roster?")) return;
    setDeleting(true);
    setError("");
    setNotice("");
    try {
      const rep = await rostersApi.remove(id);
      if (rep && rep.ok) {
        navigate("/rosters");
        return;
      }
      // eslint-disable-next-line no-alert
      alert(rep?.error || "Sorry, I couldn't delete that roster.");
    } finally {
      setDeleting(false);
    }
  }

  const title =
    roster && roster.name != null
      ? roster.name
      : roster && roster.title != null
      ? roster.title
      : id
      ? `Roster ${id}`
      : "Roster";

  // Pagination helpers
  const totalPages =
    totalCount && pageSize ? Math.max(1, Math.ceil(totalCount / pageSize)) : null;

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h1 style={{ margin: 0 }}>{title}</h1>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Link to="/rosters" style={styles.secondaryBtn}>
            Back to Rosters
          </Link>
          <button
            onClick={saveMembership}
            disabled={saving || busy || deleting}
            style={{
              ...styles.primaryBtn,
              opacity: saving || busy || deleting ? 0.7 : 1,
              pointerEvents: saving || busy || deleting ? "none" : "auto",
            }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            onClick={onDelete}
            disabled={saving || busy || deleting}
            style={{
              ...styles.deleteBtn,
              opacity: saving || busy || deleting ? 0.7 : 1,
              pointerEvents: saving || busy || deleting ? "none" : "auto",
            }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>

      {busy && <div>Loading…</div>}
      {error && <div style={styles.error}>{error}</div>}
      {notice && !error && <div style={styles.notice}>{notice}</div>}

      {!busy && (
        <>
          {/* Members list */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Members</h3>
            {memberList.length === 0 ? (
              <div style={styles.empty}>No scholars in this roster yet.</div>
            ) : (
              <ul style={styles.list}>
                {memberList.map((s) => (
                  <li key={s.id} style={styles.listItem}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{s.name}</div>
                      <div style={{ color: "#6B7280", fontSize: 12 }}>
                        {s.house ? `House: ${s.house}` : "House: —"} •{" "}
                        {s.grade ? `Grade: ${s.grade}` : "Grade: —"}
                      </div>
                    </div>
                    <button
                      style={styles.removeBtn}
                      onClick={() => onRemove(s.id)}
                      aria-label={`Remove ${s.name}`}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Filters + Search (same behavior as StudentSelectPage) */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Add Scholars</h3>

            <div style={styles.filtersRow}>
              <input
                type="text"
                placeholder="Search students by name…"
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                style={{ ...styles.input, flex: 2, minWidth: 220 }}
              />
              <select
                value={houseId}
                onChange={(e) => {
                  setPage(1);
                  setHouseId(e.target.value);
                }}
                style={{ ...styles.input, width: 160 }}
              >
                <option value="">All Houses</option>
                {houses.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </select>
              <select
                value={grade}
                onChange={(e) => {
                  setPage(1);
                  setGrade(e.target.value);
                }}
                style={{ ...styles.input, width: 120 }}
              >
                <option value="">All Grades</option>
                {allGrades.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  setSearch("");
                  setHouseId("");
                  setGrade("");
                  setPage(1);
                }}
                style={styles.secondaryBtn}
              >
                Reset
              </button>
            </div>

            {/* Results header + paging controls */}
            <div style={styles.resultsHeader}>
              <div style={{ color: "#4B5563", fontSize: 13 }}>
                {searching
                  ? "Searching…"
                  : `Showing ${results.length}${
                      typeof totalCount === "number" && totalCount > 0
                        ? ` of ${totalCount}`
                        : ""
                    } students`}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{ fontSize: 13 }}>Page Size</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);
                    setPageSize(Number(e.target.value));
                  }}
                  style={{ ...styles.input, padding: "6px 8px", width: 80 }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <button
                  style={styles.secondaryBtnSmall}
                  disabled={page <= 1 || searching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <button
                  style={styles.secondaryBtnSmall}
                  disabled={
                    searching ||
                    (totalPages ? page >= totalPages : results.length < pageSize)
                  }
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>

            {!searching && results.length > 0 && (
              <ul style={styles.results}>
                {results.map((s) => (
                  <li key={s.id} style={styles.resultItem}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{s.name}</div>
                      <div style={{ color: "#6B7280", fontSize: 12 }}>
                        {s.house ? `House: ${s.house}` : "House: —"} •{" "}
                        {s.grade ? `Grade: ${s.grade}` : "Grade: —"}
                      </div>
                    </div>
                    <button
                      style={styles.addBtn}
                      onClick={() => onAdd(s)}
                      aria-label={`Add ${s.name}`}
                    >
                      Add
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!searching &&
              (debouncedSearch || houseId || grade) &&
              results.length === 0 && (
                <div style={styles.empty}>No students found.</div>
              )}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: 900, margin: "0 auto", padding: 16 },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
    gap: 8,
  },
  section: {
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: 12,
    background: "#fff",
    marginBottom: 12,
  },
  sectionTitle: { margin: "0 0 8px 0" },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 8,
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "8px 10px",
    background: "#F9FAFB",
  },
  filtersRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 8,
  },
  resultsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    margin: "4px 0 8px",
  },
  results: {
    listStyle: "none",
    margin: "8px 0 0",
    padding: 0,
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 8,
  },
  resultItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "8px 10px",
    background: "#FFFFFF",
  },
  input: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #D1D5DB",
    background: "#fff",
  },
  primaryBtn: {
    border: "1px solid #2563EB",
    borderRadius: 8,
    padding: "8px 12px",
    background: "#2563EB",
    color: "#fff",
    textDecoration: "none",
    cursor: "pointer",
  },
  secondaryBtn: {
    border: "1px solid #6B7280",
    borderRadius: 8,
    padding: "8px 12px",
    background: "#fff",
    color: "#374151",
    textDecoration: "none",
    cursor: "pointer",
  },
  secondaryBtnSmall: {
    border: "1px solid #6B7280",
    borderRadius: 8,
    padding: "6px 10px",
    background: "#fff",
    color: "#374151",
    cursor: "pointer",
  },
  addBtn: {
    border: "1px solid #10B981",
    borderRadius: 8,
    padding: "6px 10px",
    background: "#10B981",
    color: "#fff",
    cursor: "pointer",
  },
  removeBtn: {
    border: "1px solid #c62828",
    borderRadius: 8,
    padding: "6px 10px",
    background: "#c62828",
    color: "#fff",
    cursor: "pointer",
  },
  deleteBtn: {
    border: "1px solid #c62828",
    borderRadius: 8,
    padding: "8px 12px",
    background: "#c62828",
    color: "#fff",
    cursor: "pointer",
  },
  empty: {
    border: "1px dashed #D1D5DB",
    borderRadius: 8,
    padding: 12,
    background: "#F9FAFB",
  },
  error: {
    color: "#b00020",
    background: "#FFEAEA",
    border: "1px solid #F5B5B5",
    borderRadius: 8,
    padding: "8px 10px",
    marginBottom: 12,
  },
  notice: {
    color: "#064e3b",
    background: "#ecfdf5",
    border: "1px solid #a7f3d0",
    borderRadius: 8,
    padding: "8px 10px",
    marginBottom: 12,
  },
};
